import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { FiLock, FiCheck, FiArrowLeft, FiTruck, FiCreditCard } from 'react-icons/fi';
import { trackBeginCheckout, trackPurchase } from '../utils/analytics';

const COD_MAX_AMOUNT = 2000; // COD only for orders under ₹2000

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems: cart, getSubtotal, discount, couponCode, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState('');

  // Form data
  const [shippingForm, setShippingForm] = useState({
    fullName: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    landmark: ''
  });

  const [paymentForm, setPaymentForm] = useState({
    method: 'razorpay'
  });

  const [formErrors, setFormErrors] = useState({});

  const subtotal = getSubtotal();
  const total = Math.max(0, subtotal - discount);
  const isCodAvailable = total < COD_MAX_AMOUNT;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/checkout');
      return;
    }

    if (cart.length === 0) {
      navigate('/cart');
      return;
    }

    trackBeginCheckout(cart, getSubtotal ? getSubtotal() : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, cart.length, navigate]);

  useEffect(() => {
    if (!isCodAvailable && paymentForm.method === 'cod') {
      setPaymentForm((prev) => ({ ...prev, method: 'razorpay' }));
    }
  }, [isCodAvailable, paymentForm.method]);

  const validateShippingForm = () => {
    const errors = {};
    
    if (!shippingForm.fullName.trim()) errors.fullName = 'Full name is required';
    if (!shippingForm.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(shippingForm.email)) errors.email = 'Email is invalid';
    if (!shippingForm.phone.trim()) errors.phone = 'Phone number is required';
    else if (!/^[0-9]{10}$/.test(shippingForm.phone.replace(/\D/g, ''))) errors.phone = 'Phone number must be 10 digits';
    if (!shippingForm.address.trim()) errors.address = 'Address is required';
    if (!shippingForm.city.trim()) errors.city = 'City is required';
    if (!shippingForm.state.trim()) errors.state = 'State is required';
    if (!shippingForm.pincode.trim()) errors.pincode = 'Pincode is required';
    else if (!/^[0-9]{6}$/.test(shippingForm.pincode)) errors.pincode = 'Pincode must be 6 digits';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleShippingSubmit = (e) => {
    e.preventDefault();
    if (validateShippingForm()) {
      setCurrentStep(2);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const getCartItems = () => cart.map(item => ({
    product_id: item.id,
    size: item.size,
    quantity: item.quantity
  }));

  const getShippingAddress = () => ({
    fullName: shippingForm.fullName,
    email: shippingForm.email,
    phone: shippingForm.phone,
    address: shippingForm.address,
    city: shippingForm.city,
    state: shippingForm.state,
    pincode: shippingForm.pincode,
    landmark: shippingForm.landmark
  });

  const createFinalOrder = async (paymentMethod) => {
    const orderData = {
      items: getCartItems(),
      shipping_address: getShippingAddress(),
      payment_method: paymentMethod,
      coupon_code: couponCode || null
    };

    const response = await api.auth.createOrder(orderData);

    if (response.data.success) {
      const placedOrderId = response.data.order.order_id;
      const purchaseValue =
        Number(response.data.order.total) ||
        Math.max(0, (getSubtotal ? getSubtotal() : 0) - (Number(discount) || 0));

      trackPurchase({
        orderId: placedOrderId,
        value: purchaseValue,
        items: cart,
      });

      setOrderId(placedOrderId);
      setOrderSuccess(true);
      clearCart();

      setTimeout(() => {
        navigate(`/orders/${placedOrderId}`);
      }, 3000);
    } else {
      setError(response.data.error || 'Failed to place order');
    }
  };

  const handleRazorpayPayment = async () => {
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      setError('Failed to load payment gateway. Please check your internet connection.');
      setLoading(false);
      return;
    }

    // Create Razorpay order (amount calculated server-side)
    const paymentResponse = await api.auth.createPaymentOrder({
      items: getCartItems(),
      coupon_code: couponCode || null
    });

    if (!paymentResponse.data.success) {
      setError(paymentResponse.data.error || 'Failed to initiate payment');
      setLoading(false);
      return;
    }

    const { key_id, razorpay_order_id, amount, currency } = paymentResponse.data;

    const options = {
      key: key_id,
      amount,
      currency,
      name: 'BeyondXL',
      description: 'Order Payment',
      order_id: razorpay_order_id,
      prefill: {
        name: shippingForm.fullName,
        email: shippingForm.email,
        contact: shippingForm.phone
      },
      theme: { color: '#000000' },
      handler: async (rpResponse) => {
        try {
          setLoading(true);
          await createFinalOrder({
            type: 'razorpay',
            razorpay_order_id: rpResponse.razorpay_order_id,
            razorpay_payment_id: rpResponse.razorpay_payment_id,
            razorpay_signature: rpResponse.razorpay_signature
          });
        } catch (err) {
          console.error('Order creation error after payment:', err);
          setError(api.utils.handleError(err));
        } finally {
          setLoading(false);
        }
      },
      modal: {
        ondismiss: () => {
          setError('Payment was cancelled. Your order has not been placed.');
          setLoading(false);
        }
      }
    };

    const razorpay = new window.Razorpay(options);
    razorpay.on('payment.failed', (failResponse) => {
      console.error('Razorpay payment failed:', failResponse.error);
      setError(failResponse.error?.description || 'Payment failed. Please try again.');
      setLoading(false);
    });
    razorpay.open();
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError('');

    try {
      if (paymentForm.method === 'cod' && total >= COD_MAX_AMOUNT) {
        setError(`Cash on Delivery is only available for orders under ₹${COD_MAX_AMOUNT.toLocaleString()}. Please pay online.`);
        setPaymentForm((prev) => ({ ...prev, method: 'razorpay' }));
        setLoading(false);
        return;
      }

      if (paymentForm.method === 'razorpay') {
        await handleRazorpayPayment();
        // Loading state is managed by the Razorpay callbacks
        return;
      }

      await createFinalOrder({ type: 'cod' });
      setLoading(false);
    } catch (err) {
      console.error('Order creation error:', err);
      setError(api.utils.handleError(err));
      setLoading(false);
    }
  };

  if (orderSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FiCheck className="text-green-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Order Placed Successfully!</h2>
          <p className="text-gray-600 mb-2">Your order has been confirmed.</p>
          <p className="text-lg font-semibold text-green-600 mb-6">Order ID: {orderId}</p>
          <p className="text-sm text-gray-500 mb-8">
            You will receive an email confirmation shortly. You'll be redirected to your order details...
          </p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate('/cart')}
          className="flex items-center text-gray-600 hover:text-black mb-4"
        >
          <FiArrowLeft className="mr-2" />
          Back to Cart
        </button>
        <h1 className="text-3xl font-bold">Checkout</h1>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-center space-x-8">
          <div className={`flex items-center ${currentStep >= 1 ? 'text-black' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 ${
              currentStep >= 1 ? 'bg-black text-white' : 'bg-gray-200'
            }`}>
              1
            </div>
            <span>Shipping</span>
          </div>
          <div className={`w-16 h-0.5 ${currentStep >= 2 ? 'bg-black' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center ${currentStep >= 2 ? 'text-black' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 ${
              currentStep >= 2 ? 'bg-black text-white' : 'bg-gray-200'
            }`}>
              2
            </div>
            <span>Payment</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {currentStep === 1 && (
            <div className="card">
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-6 flex items-center">
                  <FiTruck className="mr-2" />
                  Shipping Information
                </h2>
                <form onSubmit={handleShippingSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Full Name *</label>
                      <input
                        type="text"
                        value={shippingForm.fullName}
                        onChange={(e) => setShippingForm(prev => ({ ...prev, fullName: e.target.value }))}
                        className={`input-field ${formErrors.fullName ? 'border-red-500' : ''}`}
                        placeholder="John Doe"
                      />
                      {formErrors.fullName && <p className="text-red-500 text-sm mt-1">{formErrors.fullName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email *</label>
                      <input
                        type="email"
                        value={shippingForm.email}
                        onChange={(e) => setShippingForm(prev => ({ ...prev, email: e.target.value }))}
                        className={`input-field ${formErrors.email ? 'border-red-500' : ''}`}
                        placeholder="john@example.com"
                      />
                      {formErrors.email && <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      value={shippingForm.phone}
                      onChange={(e) => setShippingForm(prev => ({ ...prev, phone: e.target.value }))}
                      className={`input-field ${formErrors.phone ? 'border-red-500' : ''}`}
                      placeholder="9876543210"
                    />
                    {formErrors.phone && <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>}
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Address *</label>
                    <textarea
                      value={shippingForm.address}
                      onChange={(e) => setShippingForm(prev => ({ ...prev, address: e.target.value }))}
                      className={`input-field ${formErrors.address ? 'border-red-500' : ''}`}
                      rows="3"
                      placeholder="Street address, building, apartment"
                    />
                    {formErrors.address && <p className="text-red-500 text-sm mt-1">{formErrors.address}</p>}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">City *</label>
                      <input
                        type="text"
                        value={shippingForm.city}
                        onChange={(e) => setShippingForm(prev => ({ ...prev, city: e.target.value }))}
                        className={`input-field ${formErrors.city ? 'border-red-500' : ''}`}
                        placeholder="Mumbai"
                      />
                      {formErrors.city && <p className="text-red-500 text-sm mt-1">{formErrors.city}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">State *</label>
                      <input
                        type="text"
                        value={shippingForm.state}
                        onChange={(e) => setShippingForm(prev => ({ ...prev, state: e.target.value }))}
                        className={`input-field ${formErrors.state ? 'border-red-500' : ''}`}
                        placeholder="Maharashtra"
                      />
                      {formErrors.state && <p className="text-red-500 text-sm mt-1">{formErrors.state}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Pincode *</label>
                      <input
                        type="text"
                        value={shippingForm.pincode}
                        onChange={(e) => setShippingForm(prev => ({ ...prev, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                        className={`input-field ${formErrors.pincode ? 'border-red-500' : ''}`}
                        placeholder="400001"
                      />
                      {formErrors.pincode && <p className="text-red-500 text-sm mt-1">{formErrors.pincode}</p>}
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">Landmark (Optional)</label>
                    <input
                      type="text"
                      value={shippingForm.landmark}
                      onChange={(e) => setShippingForm(prev => ({ ...prev, landmark: e.target.value }))}
                      className="input-field"
                      placeholder="Near railway station"
                    />
                  </div>
                  
                  <button type="submit" className="btn-primary w-full">
                    Continue to Payment
                  </button>
                </form>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="card">
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-6 flex items-center">
                  <FiCreditCard className="mr-2" />
                  Payment Information
                </h2>
                
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                  </div>
                )}
                
                <form onSubmit={handlePlaceOrder}>
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-3">Payment Method</label>
                    <div className="space-y-2">
                      <label
                        className={`flex items-center p-4 border rounded-lg ${
                          isCodAvailable
                            ? 'hover:bg-gray-50 cursor-pointer'
                            : 'opacity-60 cursor-not-allowed bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="cod"
                          checked={paymentForm.method === 'cod'}
                          disabled={!isCodAvailable}
                          onChange={(e) => setPaymentForm(prev => ({ ...prev, method: e.target.value }))}
                          className="mr-3"
                        />
                        <div>
                          <div className="font-medium">Cash on Delivery</div>
                          <div className="text-sm text-gray-600">
                            {isCodAvailable
                              ? 'Pay when your order arrives'
                              : `Available only for orders under ₹${COD_MAX_AMOUNT.toLocaleString()}`}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-center p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="razorpay"
                          checked={paymentForm.method === 'razorpay'}
                          onChange={(e) => setPaymentForm(prev => ({ ...prev, method: e.target.value }))}
                          className="mr-3"
                        />
                        <div>
                          <div className="font-medium">Pay Online (UPI / Card / NetBanking)</div>
                          <div className="text-sm text-gray-600">Secure payment via Razorpay</div>
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  <div className="flex space-x-4">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="btn-secondary flex-1"
                    >
                      Back to Shipping
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary flex-1 flex items-center justify-center"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <FiLock className="mr-2" size={16} />
                          {paymentForm.method === 'razorpay' ? `Pay ₹${total.toLocaleString()}` : 'Place Order'}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="card sticky top-24">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
              
              {/* Cart Items */}
              <div className="space-y-4 mb-6">
                {cart.map((item) => (
                  <div key={`${item.id}-${item.size}`} className="flex items-center space-x-3">
                    <img
                      src={api.utils.getImageUrl(item.images?.[0], { width: 1200, height: 1500, fit: 'cover' })}
                      alt={item.name}
                      className="w-16 aspect-[4/5] object-cover object-center rounded-lg"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=100&h=100&fit=crop';
                      }}
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{item.name}</h4>
                      <p className="text-gray-600 text-sm">Size: {item.size}</p>
                      <p className="text-gray-600 text-sm">Qty: {item.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">₹{(item.price * item.quantity).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <hr className="mb-4" />
              
              {/* Order Totals */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>₹{subtotal.toLocaleString()}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount ({couponCode})</span>
                    <span>-₹{discount}</span>
                  </div>
                )}
                <hr className="my-2" />
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span>₹{total.toLocaleString()}</span>
                </div>
              </div>
              
              <div className="mt-6 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center text-sm text-gray-600">
                  <FiLock className="mr-2" size={16} />
                  <span>Secure checkout powered by SSL encryption</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout; 